import { cn } from '@/lib/utils';
import { ShowBase, useRecordContext, useTranslate } from "ra-core";
import { abasBentoGatilho, abasBentoLista } from "../bento/ui";
import { formatBRL, formatBrDateLocal, formatBrDateTime } from "@/lib/format";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Mail, MapPin, Calendar, Tag, CreditCard, Building2, Briefcase, DollarSign, Heart, MessageCircle } from "lucide-react";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceManyField } from "@/components/admin/reference-many-field";
import { TextField } from "@/components/admin/text-field";
import { DateField } from "@/components/admin/date-field";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEffect, useState } from "react";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate, NotesIterator } from "../notes";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { TagsListEdit } from "./TagsListEdit";
import { getContactDetail, getContactDisplayName } from "./contact-details";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { SaleName } from "../vendas/components/SaleName";
import { useProductNavigate } from "../misc/useProductNavigate";
import { SegurosDoContato } from "../seguros/SegurosDoContato";
import { PropostasDoContato } from "../seguros/PropostasPlataforma";
import { useGetList, useCreatePath } from "ra-core";
import { formatDocument } from "./document-validation";
import { generateProposalPdf } from "../consortium-groups/proposal-utils";
import { PorQueConsorcioDialog } from "../consortium-groups/PorQueConsorcioDialog";
import type { ProposalPayload, UserInfo } from "../consortium-groups/proposal-utils";
import { LanceStatusBadge } from "../misc/LanceStatusBadge";
import { logAuditEvent } from "../auditoria/logAudit";
import { TagsList } from "./TagsList";
import { useSomenteLeitura } from "../commons/somenteLeitura";
import { GrupoLink } from "../consortium-groups/GrupoLink";
import { ContactEditDialog } from "./ContactEdit";
import { BotaoPlaybook } from "../ai/playbook/BotaoPlaybook";
import { AcoesGoogleContato } from "../conexoes/AcoesGoogleContato";

export const ContactShow = () => (
  <ShowBase resource="contacts">
    <ContactShowContent />
  </ShowBase>
);

/** `embutida`: dentro de outra tela (o lead do CRM), onde "Voltar" não tem para onde voltar. */
export const ContactShowContent = ({ embutida = false }: { embutida?: boolean }) => {
  const { somenteLeitura } = useSomenteLeitura();
  const record = useRecordContext<Contact>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const { contactGender } = useConfigurationContext();
  const [editando, setEditando] = useState(false);

  // Auditoria LGPD: registra a visualização dos dados do cliente (uma vez por
  // registro aberto). A edição é capturada por trigger no banco.
  useEffect(() => {
    if (!record?.id) return;
    void logAuditEvent({
      entity: "cliente",
      action: "view",
      entityId: record.id,
      summary: getContactDisplayName(record),
      // Colapsa visualizações repetidas do mesmo vendedor no mesmo cliente
      // numa linha só (atualiza a hora + conta acessos).
      dedupe: true,
    });
  }, [record?.id]);

  if (!record) return null;

  const email = getContactDetail(record, "email");
  const phone = getContactDetail(record, "phone");
  const cpf = getContactDetail(record, "cpf");
  const personType = getContactDetail(record, "person_type") || "PF";
  const birthDate = getContactDetail(record, "birth_date");
  const maritalStatus = getContactDetail(record, "marital_status");
  const monthlyIncome = getContactDetail(record, "monthly_income");
  const monthlyIncomeNumber = monthlyIncome != null ? Number(monthlyIncome) : null;
  const contactPreference = getContactDetail(record, "contact_preference");
  const title = getContactDetail(record, "title");
  const company = getContactDetail(record, "company");
  const genderValue = getContactDetail(record, "gender");
  const background = getContactDetail(record, "background");
  
  // Address fields
  const zipcode = getContactDetail(record, "zipcode");
  const address = getContactDetail(record, "address");
  const addressNumber = getContactDetail(record, "address_number");
  const addressComplement = getContactDetail(record, "address_complement");
  const neighborhood = getContactDetail(record, "neighborhood");
  const city = getContactDetail(record, "city");
  const state = getContactDetail(record, "state");
  
  const genderOption = contactGender.find(g => g.value === genderValue);

  const maritalStatusLabels: Record<string, string> = {
    single: "Solteiro(a)",
    married: "Casado(a)",
    divorced: "Divorciado(a)",
    widowed: "Viúvo(a)",
    other: "Outro",
  };

  const contactPreferenceLabels: Record<string, string> = {
    email: "Email",
    phone: "Telefone",
    whatsapp: "WhatsApp",
  };

  const formatCurrency = (value?: number) => {
    if (!value) return null;
    return formatBRL(value);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    return formatBrDateLocal(dateStr);
  };

  const InfoItem = ({ label, value, icon }: { label: string; value?: string | null; icon?: React.ReactNode }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3 py-2">
        {icon && <div className="text-muted-foreground mt-0.5">{icon}</div>}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground mb-0.5">{label}</p>
          <p className="text-sm font-medium break-words">{value}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-2 mb-2 max-w-7xl mx-auto">
      {record.arquivado_em && (
        <div role="status" className="mb-3 rounded-lg border border-[#F3DC9C] bg-alerta-fundo px-3 py-2 text-sm font-medium text-alerta">
          {record.arquivado_motivo === "transferido"
            ? "Contato transferido para outra base por autorização do administrador. O histórico permanece disponível."
            : "Contato arquivado pelo administrador (celular repetido). O histórico permanece disponível."}{" "}
          Arquivado em {formatBrDateLocal(record.arquivado_em)}.
        </div>
      )}
      {editando && (
        <ContactEditDialog
          open
          onClose={() => setEditando(false)}
          // Contato passado para outra pessoa pode ter saído do escopo: volta
          // para a lista de onde se veio (o quadro, quando a ficha está no lead).
          aoTrocarResponsavel={() => navigate(embutida ? "/deals" : "/contacts")}
        />
      )}
      {/* Header */}
      <div className={`mb-4 flex flex-wrap items-center gap-2 ${embutida ? "justify-end" : "justify-between"}`}>
        {!embutida && (
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar
          </Button>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {/* Perfil de leitura não conversa com a IA: o banco barra a conversa (migration 184). */}
          {!somenteLeitura && (
            <BotaoPlaybook alvo={{ tipo: "contato", id: record.id, nome: getContactDisplayName(record) }} />
          )}
          {!somenteLeitura && (
            <AcoesGoogleContato
              contato={{ id: record.id, nome: getContactDisplayName(record), email: (record.details?.email as string) ?? null }}
            />
          )}
          {!somenteLeitura && (
            <Button variant="outline" onClick={() => setEditando(true)}>
              <Pencil />
              {translate("atomic.contacts.editContact")}
            </Button>
          )}
        </div>
      </div>

      {/* Cabeçalho do contato (Premium North): o mesmo conteúdo sobre o degradê
          marinho — nome, cargo e empresa, e-mail e WhatsApp clicáveis. */}
      <div className="cabecalho-premium relative mb-6 overflow-hidden rounded-[14px] bg-premium px-5 py-5 text-white sm:px-6">
        <span
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full border border-ouro/35 shadow-[0_0_0_40px_rgba(196,173,142,0.05)]"
        />
        <div className="relative flex items-start gap-4">
          <div className="rounded-full ring-2 ring-ouro/60">
            <Avatar />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="mb-1 text-[24px] font-bold leading-tight tracking-[-0.02em] text-white sm:text-[26px]">
              {getContactDisplayName(record)}
            </h1>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-white/72 [&_a]:text-white">
              {title && (
                <span className="flex items-center gap-1 text-[15px]">
                  <Briefcase className="h-4 w-4" />
                  {title}
                </span>
              )}
              {(title || company) && <span>•</span>}
              {company && (
                <span className="flex items-center gap-1 text-[15px]">
                  <Building2 className="h-4 w-4" />
                  {company}
                </span>
              )}
              {record.company_id != null && (
                <>
                  {(title || company) && <span>•</span>}
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link="show"
                    className="text-[15px]"
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-4">
              {email && !somenteLeitura && (
                <a href={`mailto:${email}`} className="flex items-center gap-2 break-all text-sm text-ouro hover:underline">
                  <Mail className="h-4 w-4 shrink-0" />
                  {email}
                </a>
              )}
              {phone && (
                <a href={`https://wa.me/55${phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-ouro hover:underline">
                  <MessageCircle className="h-4 w-4 shrink-0" />
                  {phone}
                </a>
              )}
            </div>
          </div>
          {record.company_id != null && (
            <ReferenceField
              source="company_id"
              reference="companies"
              link="show"
              className="no-underline"
            >
              <CompanyAvatar />
            </ReferenceField>
          )}
        </div>
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Personal Data Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Dados pessoais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoItem 
              label={personType === "PJ" ? "CNPJ" : "CPF"} 
              value={cpf ? formatDocument(cpf, personType) : null}
            />
            <InfoItem 
              label={personType === "PJ" ? "Data de Criação" : "Data de Nascimento"} 
              value={birthDate ? formatDate(birthDate) : null}
            />
            {genderOption && (
              <InfoItem 
                label="Gênero" 
                value={translate(genderOption.label)}
              />
            )}
            {maritalStatus && (
              <InfoItem 
                label="Estado Civil" 
                value={maritalStatusLabels[maritalStatus] || maritalStatus}
                icon={<Heart className="w-4 h-4" />}
              />
            )}
            {monthlyIncome && (
              <InfoItem 
                label="Renda Mensal" 
                value={monthlyIncomeNumber != null && Number.isFinite(monthlyIncomeNumber) ? formatCurrency(monthlyIncomeNumber) : null}
                icon={<DollarSign className="w-4 h-4" />}
              />
            )}
            {contactPreference && (
              <InfoItem 
                label="Preferência de Contato" 
                value={contactPreferenceLabels[contactPreference] || contactPreference}
              />
            )}
            {background && (
              <>
                <Separator className="my-3" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Informações adicionais</p>
                  <p className="text-sm whitespace-pre-wrap">{background}</p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Address Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Endereço
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {address && (
              <InfoItem 
                label="Logradouro" 
                value={`${address}${addressNumber ? `, ${addressNumber}` : ''}`}
              />
            )}
            <InfoItem 
              label="Complemento" 
              value={addressComplement}
            />
            <InfoItem 
              label="Bairro" 
              value={neighborhood}
            />
            <InfoItem 
              label="Cidade" 
              value={city}
            />
            <InfoItem 
              label="Estado" 
              value={state}
            />
            <InfoItem 
              label="CEP" 
              value={zipcode}
            />
            {(!address && !city && !zipcode) && (
              <p className="text-sm text-muted-foreground py-2">Nenhum endereço cadastrado.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Activity & Tags Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Activity Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Atividade
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="py-2">
              <p className="text-xs text-muted-foreground mb-1">Adicionado em</p>
              <DateField
                source="first_seen"
                options={{ year: "numeric", month: "long", day: "numeric" }}
                className="text-sm font-medium"
              />
            </div>
            <div className="py-2">
              <p className="text-xs text-muted-foreground mb-1">Última atividade</p>
              <DateField
                source="last_seen"
                options={{ year: "numeric", month: "long", day: "numeric" }}
                className="text-sm font-medium"
              />
            </div>
            <div className="py-2">
              <p className="text-xs text-muted-foreground mb-1">Acompanhado por</p>
              <div className="text-sm font-medium">
                <ReferenceField source="user_id" reference="users">
                  <SaleName />
                </ReferenceField>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tags Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Tags
            </CardTitle>
          </CardHeader>
          <CardContent>
            {somenteLeitura ? <TagsList /> : <TagsListEdit />}
          </CardContent>
        </Card>
      </div>

      {/* Seguros — o contato é compartilhado entre os produtos, então cada
          módulo mostra o que o outro tem. Some sozinho quando não há seguro. */}
      <SegurosDoContato cpf={cpf} />
      {/* As propostas que a plataforma fez para esta pessoa (cotação →
          proposta), com situação, documentos e pendências. Some sem proposta. */}
      <PropostasDoContato contatoId={record.id} />

      {/* Quotas History */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Histórico de cotas compradas</CardTitle>
        </CardHeader>
        <CardContent>
          <ContactQuotaHistory />
        </CardContent>
      </Card>

      {/* Notes Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Propostas geradas</CardTitle>
        </CardHeader>
        <CardContent>
          <ContactProposalHistory />
        </CardContent>
      </Card>

      {/* Notes Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Notas</CardTitle>
        </CardHeader>
        <CardContent>
          <ReferenceManyField
            target="contact_id"
            reference="contactNotes"
            sort={{ field: "date", order: "DESC" }}
            empty={
              somenteLeitura ? null : <NoteCreate reference="contacts" />
            }
          >
            <NotesIterator reference="contacts" />
          </ReferenceManyField>
        </CardContent>
      </Card>
    </div>
  );
};

// Helper components for sales and payments
const formatCurrency = (value?: number) => {
  if (!value && value !== 0) return "-";
  return formatBRL(value);
};

const formatDate = (value?: string) => {
  if (!value) return "-";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
  }
  return formatBrDateLocal(value);
};

const statusLabels: Record<string, string> = {
  active: "Ativa",
  confirmed: "Ativa",
  confirmado: "Ativa",
  cancelled: "Cancelado",
  canceled: "Cancelado",
  expired: "Expirado",
  renewed: "Renovado",
  pending: "Pendente",
  started: "Pendente",
  reconciled: "Pendente",
  failed: "Falhou",
  success: "Sucesso",
};

const normalizeSaleStatus = (value: unknown) => String(value || "").trim().toLowerCase();
const isPendingSaleStatus = (value: unknown) => ["pending", "started", "reconciled"].includes(normalizeSaleStatus(value));

const ContactQuotaHistory = () => {
  const record = useRecordContext<Contact>();
  const irParaProduto = useProductNavigate();
  const createPath = useCreatePath();
  const [statusFilter, setStatusFilter] = useState<"primary" | "all" | "pending" | "cancelled">("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const { data, isLoading } = useGetList(
    "servopa_vendas_with_details",
    {
      filter: { contact_id: record?.id },
      sort: { field: "venda_data", order: "DESC" },
      pagination: { page: 1, perPage: 50 },
    },
    { enabled: !!record?.id }
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  if (isLoading) return <div className="text-sm text-muted-foreground">Carregando...</div>;
  if (!data || data.length === 0) {
    return <div className="text-sm text-muted-foreground">Nenhuma cota encontrada.</div>;
  }

  const filteredData =
    statusFilter === "all"
      ? data
      : statusFilter === "primary"
        ? data.filter((sale: any) => ["active", "confirmed", "confirmado"].includes(normalizeSaleStatus(sale.status)))
        : statusFilter === "pending"
          ? data.filter((sale: any) => isPendingSaleStatus(sale.status))
          : data.filter((sale: any) => normalizeSaleStatus(sale.status) === statusFilter);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const openSaleHistory = (sale: any) => {
    const salesPath = createPath({ resource: "vendas", type: "list" });
    const isPending = isPendingSaleStatus(sale.status);
    const query = new URLSearchParams();
    const contrato = sale.contrato ? String(sale.contrato) : "";
    const fallbackReference = [
      sale.group_number ? `Grupo ${sale.group_number}` : null,
      sale.cota ? `Cota ${sale.cota}` : null,
    ]
      .filter(Boolean)
      .join(" • ");

    query.set("tab", isPending ? "pending" : "completed");

    if (contrato) {
      query.set("contrato", contrato);
    } else {
      query.set("sale_id", String(sale.id));
      if (fallbackReference) {
        query.set("ref", fallbackReference);
      }
    }

    // Venda é do consórcio: fora dele, sai da origem atual.
    irParaProduto("consortium", `${salesPath}?${query.toString()}`);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Filtrar histórico por status
      </div>
      <Tabs
        value={statusFilter}
        onValueChange={(value) => setStatusFilter(value as "primary" | "all" | "pending" | "cancelled")}
        className="mb-2"
      >
        <TabsList className={cn(abasBentoLista, "flex-wrap gap-y-1 overflow-visible")}>
          <TabsTrigger
            value="all"
            className={abasBentoGatilho}
          >
            Todas
          </TabsTrigger>
          <TabsTrigger
            value="primary"
            className={abasBentoGatilho}
          >
            Ativas
          </TabsTrigger>
          <TabsTrigger
            value="pending"
            className={abasBentoGatilho}
          >
            Pendentes
          </TabsTrigger>
          <TabsTrigger
            value="cancelled"
            className={abasBentoGatilho}
          >
            Canceladas
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {filteredData.length === 0 && (
        <div className="text-sm text-muted-foreground">Nenhuma cota no filtro selecionado.</div>
      )}

      {paginatedData.map((sale: any) => {
        const normalizedStatus = normalizeSaleStatus(sale.status);
        const statusLabel = statusLabels[normalizedStatus] || sale.status || "-";

        return (
          <button
            key={sale.id}
            type="button"
            onClick={() => openSaleHistory(sale)}
            className="w-full cursor-pointer rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/50"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="text-[17px] font-semibold leading-tight text-marinho">
                Contrato {sale.contrato || "-"} • Cota {sale.cota || "-"}
              </div>
              <div className="flex items-center gap-2">
                {normalizedStatus === "confirmed" && (
                  <LanceStatusBadge
                    status={sale.next_lance_status}
                    tooltip={sale.next_lance_error}
                  />
                )}
                <span className="bg-secondary px-3 py-1.5 rounded text-sm font-medium">{statusLabel}</span>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-muted-foreground">
              <div>
                <span className="font-semibold text-foreground">Grupo:</span>{" "}
                {sale.group_number ? (
                  <GrupoLink groupNumber={sale.group_number} />
                ) : (
                  "-"
                )}
              </div>
              <div>
                <span className="font-semibold text-foreground">Data venda:</span> {formatDate(sale.venda_data)}
              </div>
              <div>
                <span className="font-semibold text-foreground">Parcela:</span> {formatCurrency(Number(sale.valor_parcela))}
              </div>
              <div>
                <span className="font-semibold text-foreground">Crédito:</span> {formatCurrency(Number(sale.valor_credito))}
              </div>
            </div>
          </button>
        );
      })}

      {filteredData.length > itemsPerPage && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            Página {currentPage} de {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              Anterior
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ContactProposalHistory = () => {
  const { somenteLeitura } = useSomenteLeitura();
  const record = useRecordContext<Contact>();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [porQue, setPorQue] = useState<{ payload: ProposalPayload; userInfo: UserInfo } | null>(null);
  const { data, isLoading } = useGetList(
    "proposal_simulations",
    {
      filter: { contact_id: record?.id },
      sort: { field: "generated_at", order: "DESC" },
      pagination: { page: 1, perPage: 20 },
    },
    { enabled: !!record?.id },
  );

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Carregando...</div>;
  }

  if (!data || data.length === 0) {
    return <div className="text-sm text-muted-foreground">Nenhuma proposta salva.</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      <PorQueConsorcioDialog
        payload={porQue?.payload ?? null}
        userInfo={porQue?.userInfo ?? {}}
        onClose={() => setPorQue(null)}
      />
      {data.map((proposal: any) => {
        const proposalPayload = proposal.proposal_payload || {};
        const advisorSnapshot = proposal.advisor_snapshot || {};
        const generatedAt = proposal.generated_at
          ? formatBrDateTime(proposal.generated_at)
          : "-";
        const categoryLabel = proposal.category_code || "Categoria não informada";

        return (
          <div key={proposal.id} className="rounded-lg border border-border p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold">
                  {proposal.group_number ? (
                    <>
                      Grupo{" "}
                      <GrupoLink groupNumber={proposal.group_number} />
                    </>
                  ) : (
                    "Grupo não informado"
                  )}{" "}
                  • {categoryLabel}
                </div>
                <div className="text-xs text-muted-foreground">Gerada em {generatedAt}</div>
              </div>
              {!somenteLeitura && <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={downloadingId === proposal.id}
                onClick={async () => {
                  setDownloadingId(proposal.id);
                  try {
                    await generateProposalPdf(proposalPayload, advisorSnapshot);
                  } finally {
                    setDownloadingId(null);
                  }
                }}
              >
                {downloadingId === proposal.id ? "Gerando..." : "Gerar PDF novamente"}
              </Button>}
              {!somenteLeitura && proposalPayload.valorCredito && !Array.isArray(proposalPayload.cotas) && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPorQue({ payload: proposalPayload, userInfo: advisorSnapshot })}
                >
                  Apresentação
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
